import {IQuestionsService} from './IQuestionsService';
import {QuestionsRepository} from './QuestionsRepository';
import {QuestionsNewRepository} from './QuestionsNewRepository';
import {AssetUpdate, Question, QuestionUpdate} from '../../graphql/API';
import type {QuestionCreateInput, QuestionUpdateInput, QuestionWithNewFields} from './QuestionTypes';
import {AssetsRepository} from '../Asset/AssetsRepository';
import {getCurrentUser} from 'aws-amplify/auth';
import { stripTypename } from '../../utils';



export class QuestionsService implements IQuestionsService {
  questionsRepository: QuestionsRepository;
  questionsNewRepository: QuestionsNewRepository;
  assetsRepository: AssetsRepository;
  constructor() {
    this.questionsRepository = new QuestionsRepository();
    this.questionsNewRepository = new QuestionsNewRepository();
    this.assetsRepository = new AssetsRepository();
  }
  // OLD CODE - Commented out (using old createQuestion method)
  // async createQuestion(question: Question): Promise<any> {
  //   //console.log('Qchoics', question.customChoices);
  //   let questionObj = {
  //     name: question.name,
  //     summary: question.summary,
  //     description: question.description,
  //     text: question.text,
  //     correctAssetId: '',
  //     assetId: '',
  //     programType: question.programType,
  //     choices: question.choices?.length !== 0 ? question.choices : [],
  //     customChoices:
  //       question?.customChoices &&
  //       Object.keys(question?.customChoices).length > 0
  //         ? JSON.stringify(question.customChoices)
  //         : undefined,
  //     keywords: [],
  //     type: question.type,
  //     textAI: question.textAI,
  //   };
  //   if (
  //     question.asset.fileUri.length !== 0 ||
  //     question.asset.thumbnailUri.length !== 0
  //   ) {
  //     questionObj.assetId = await this.assetsRepository.createAsset(
  //       question.asset!,
  //     );
  //   }
  //   if (question.correctAsset?.fileUri?.length !== 0) {
  //     questionObj.correctAssetId = await this.assetsRepository.createAsset(
  //       question.correctAsset!,
  //     );
  //   }
  //
  //   return await this.questionsRepository.createQuestion(questionObj);
  // }

  // NEW CODE - Using newCreateQuestion from @PikaDoApp/npm-pikado-logic
  async createQuestion(question: QuestionCreateInput): Promise<string> {
    try {
      // REMOVABLE: Debug logging - remove before production
      console.log('=== CREATE QUESTION DEBUG ===');
      console.log('Received question data:', question);
      console.log('Question type:', question.type);
      console.log('Custom choices:', question.customChoices);
      console.log('Correct index:', question.correctIndex);
      console.log('Asset:', question.asset);
      console.log('Correct asset:', question.correctAsset);

      // Get current user for createdBy field
      let createdBy = '';
      try {
        const user = await getCurrentUser();
        createdBy = user.signInDetails?.loginId || user.userId || 'unknown';
      } catch (error) {
        console.error('Error getting current user:', error);
        createdBy = 'unknown';
      }

      // Build payload object for v1.1.22
      const payload: any = {};

      // Build blanks array for COMPREHENSION type
      if (question.type === 'COMPREHENSION' &&
          question.customChoices &&
          Object.keys(question.customChoices).length > 0) {
        // COMPREHENSION: Transform customChoices + correctIndex → payload.blanks[]
        // Filter out blanks with empty or invalid choices
        const blanksArray = Object.entries(question.customChoices)
          .filter(([name, choices]) => Array.isArray(choices) && choices.length > 0)
          .map(([name, choices]) => {
            const correctIndex = question.correctIndex?.[name] ?? 0;
            return {
              name: name,
              choices: choices as string[],
              correctAnswer: (choices as string[])[correctIndex] || (choices as string[])[0],
            };
          });

        if (blanksArray.length > 0) {
          payload.blanks = blanksArray;
        }

        // REMOVABLE: Debug logging
        console.log('Blanks array created:', blanksArray);
      }

      // Build media array from asset and correctAsset
      const media: Array<{ type: string; url: string }> = [];

      // Validate and add question asset (image/video)
      // For PHOTO type, if fileUri is empty but thumbnailUri exists, use thumbnailUri
      if (question.asset) {
        let assetUrl = question.asset.fileUri?.trim() || '';

        // If fileUri is empty but thumbnailUri exists (especially for PHOTO type), use thumbnailUri
        if (!assetUrl && question.asset.thumbnailUri?.trim()) {
          assetUrl = question.asset.thumbnailUri.trim();
          // REMOVABLE: Debug logging
          console.log('Using thumbnailUri as fileUri for asset:', assetUrl);
        }

        if (assetUrl) {
          media.push({
            type: question.asset.type || 'IMAGE',
            url: assetUrl,
          });
        }
      }

      // Validate and add correct answer asset (audio)
      if (question.correctAsset?.fileUri && question.correctAsset.fileUri.trim() !== '') {
        media.push({
          type: question.correctAsset.type || 'AUDIO',
          url: question.correctAsset.fileUri.trim(),
        });
      }

      if (media.length > 0) {
        payload.media = media;
      }

      // REMOVABLE: Debug logging
      console.log('Media array created:', media);
      console.log('Final payload object:', payload);

      // Map old field names to new ones
      // text -> questionText
      // textAI -> AiText
      // program -> program (needs to be MongoDB _id)
      const questionObj: any = {
        program: question.program || '', // Program MongoDB ID required (no fallback to programType)
        questionText: question.text || question.questionText || '',
        AiText: question.textAI || question.AiText || '',
        type: question.type || 'COMPREHENSION', // QuestionType enum
        description: question.description || '',
        difficulty: question.difficulty || 'NORMAL', // Difficulty enum: EASY, NORMAL, HARD
        createdBy: createdBy,
        name: question.name || '', // Add name field
        summary: question.summary || '', // Add summary field
      };

      // Add payload if it has content
      if (Object.keys(payload).length > 0) {
        questionObj.payload = payload;
      }

      // REMOVABLE: Debug logging - Final object being sent to API
      console.log('Final questionObj being sent to API:', questionObj);
      console.log('===========================');

      console.log('Creating question with pikado logic:', questionObj);
      const createdQuestion = await this.questionsNewRepository.createQuestion(questionObj);

      // Return the ID for compatibility with existing code
      return createdQuestion._id || createdQuestion.id || '';
    } catch (error) {
      console.error('Creating question error:', error);
      throw error;
    }
  }

  // OLD CODE - Commented out (using old updateQuestion method)
  // async updateQuestion(
  //   id: string,
  //   question: Question,
  //   updatedAt: string,
  // ): Promise<any> {
  //   let questionObj: QuestionUpdate = {
  //     name: question.name,
  //     summary: question.summary,
  //     description: question.description,
  //     text: question.text,
  //     textAI: question.textAI,
  //     type: question.type,
  //     choices: question.choices?.length !== 0 ? question.choices : [],
  //     customChoices:
  //       question?.customChoices &&
  //       Object.keys(question?.customChoices).length > 0
  //         ? JSON.stringify(question.customChoices)
  //         : undefined,
  //     videoAISessionUri: question?.videoAISessionUri,
  //   };
  //   //console.log('question.asset', question.asset);
  //   if (
  //     question.asset &&
  //     !question?.asset?.id &&
  //     (question?.asset.thumbnailUri || question?.asset.fileUri)
  //   ) {
  //     questionObj.assetId = await this.assetsRepository.createAsset(
  //       question.asset!,
  //     );
  //   } else if (question?.asset?.id) {
  //     const assetUpdate: AssetUpdate = {
  //       type: question.asset.type,
  //       thumbnailUri: question.asset.thumbnailUri,
  //       fileUri: question.asset.fileUri,
  //       title_i18n: question.asset.title_i18n,
  //       subTitle_i18n: question.asset.subTitle_i18n,
  //       position: question.asset.position,
  //     };
  //     await this.assetsRepository.updateAsset(
  //       question?.asset?.id,
  //       question?.asset?.updatedAt,
  //       assetUpdate,
  //     );
  //   }
  //   // if (question.correctAsset?.fileUri?.length !== 0) {
  //   //   questionObj.correctAssetId = await this.assetsRepository.createAsset(
  //   //     question.correctAsset!,
  //   //   );
  //   // }
  //
  //   return await this.questionsRepository.updateQuestion(
  //     id,
  //     questionObj,
  //     updatedAt,
  //   );
  // }

  // NEW CODE - Using newUpdateQuestion from @PikaDoApp/npm-pikado-logic
  async updateQuestion(
    id: string,
    question: QuestionUpdateInput,
    updatedAt?: string,
  ): Promise<QuestionWithNewFields | null> {
    try {
      // Strip __typename from entire question object (GraphQL metadata)
      const cleanQuestion = stripTypename(question);

      // Map old field names to new ones
      const questionObj: any = {};

      // Only include fields that are provided
      if (cleanQuestion.text || cleanQuestion.questionText) {
        questionObj.questionText = cleanQuestion.text || cleanQuestion.questionText;
      }
      if (cleanQuestion.textAI || cleanQuestion.AiText) {
        questionObj.AiText = cleanQuestion.textAI || cleanQuestion.AiText;
      }
      if (cleanQuestion.type) {
        questionObj.type = cleanQuestion.type;
      }
      if (cleanQuestion.description) {
        questionObj.description = cleanQuestion.description;
      }
      if (cleanQuestion.difficulty) {
        questionObj.difficulty = cleanQuestion.difficulty;
      }
      if (cleanQuestion.name) {
        questionObj.name = cleanQuestion.name;
      }
      if (cleanQuestion.summary) {
        questionObj.summary = cleanQuestion.summary;
      }
      // Include program field to prevent null errors in GraphQL
      if (cleanQuestion.program) {
        questionObj.program = typeof cleanQuestion.program === 'object'
          ? cleanQuestion.program._id
          : cleanQuestion.program;
      }

      // Build payload object for v1.1.22 if updating choices/media
      const payload: any = {};

      // Build blanks array for COMPREHENSION type
      if (cleanQuestion.type === 'COMPREHENSION' && cleanQuestion.payload?.blanks) {
        // Use payload.blanks directly (new structure) - already clean from stripTypename
        payload.blanks = cleanQuestion.payload.blanks
          .filter((blank: any) => blank.choices && blank.choices.length > 0)
          .map(({ name, choices, correctAnswer }: any) => ({
            name,
            choices,
            correctAnswer
          }));
      }

      // Build media array from asset and correctAsset
      const media: Array<{ type: string; url: string }> = [];

      // Validate and add question asset (image/video)
      // For PHOTO type, if fileUri is empty but thumbnailUri exists, use thumbnailUri
      if (cleanQuestion.asset) {
        let assetUrl = cleanQuestion.asset.fileUri?.trim() || '';

        // If fileUri is empty but thumbnailUri exists (especially for PHOTO type), use thumbnailUri
        if (!assetUrl && cleanQuestion.asset.thumbnailUri?.trim()) {
          assetUrl = cleanQuestion.asset.thumbnailUri.trim();
        }

        if (assetUrl) {
          media.push({
            type: cleanQuestion.asset.type || 'IMAGE',
            url: assetUrl,
          });
        }
      }

      // Validate and add correct answer asset (audio)
      if (cleanQuestion.correctAsset?.fileUri && cleanQuestion.correctAsset.fileUri.trim() !== '') {
        media.push({
          type: cleanQuestion.correctAsset.type || 'AUDIO',
          url: cleanQuestion.correctAsset.fileUri.trim(),
        });
      }

      // ✨ NEW: If no new media was provided, preserve existing payload.media
      if (media.length === 0 && cleanQuestion.payload?.media && Array.isArray(cleanQuestion.payload.media)) {
        console.log('No new media provided, preserving existing payload.media:', cleanQuestion.payload.media);
        media.push(...cleanQuestion.payload.media);
      }

      if (media.length > 0) {
        payload.media = media;
      }

      // Add payload if it has content
      if (Object.keys(payload).length > 0) {
        questionObj.payload = payload;
      }

      console.log('Updating question with pikado logic:', questionObj);
      const updatedQuestion = await this.questionsNewRepository.updateQuestion(
        id,
        questionObj,
        updatedAt,
      );

      return updatedQuestion;
    } catch (error) {
      console.error('Updating question error:', error);
      throw error;
    }
  }

  // Get question by ID using getQuestionById from @PikaDoApp/npm-pikado-logic
  async getQuestionById(id: string): Promise<QuestionWithNewFields | null> {
    try {
      console.log('Getting question by ID with pikado logic:', id);
      const question = await this.questionsNewRepository.getQuestionById(id);

      if (!question) {
        return null;
      }

      // Transform MongoDB format to UI format
      // Spread all fields first to preserve everything, then override specific mappings
      const transformedQuestion: any = {
        ...question,  // Preserve all fields from the API
        id: question._id || question.id,
        _id: question._id,
        text: question.questionText || question.text || '',
        textAI: question.AiText || question.textAI || '',
        program: question.program?._id || question.program || '',
      };

      // Transform payload.media to asset and correctAsset
      if (question.payload?.media && Array.isArray(question.payload.media)) {
        question.payload.media.forEach((media: any) => {
          if (media.type === 'IMAGE' || media.type === 'VIDEO') {
            transformedQuestion.asset = {
              fileUri: media.url,
              thumbnailUri: media.url,
              type: media.type,
            };
          } else if (media.type === 'AUDIO') {
            transformedQuestion.correctAsset = {
              fileUri: media.url,
              type: media.type,
            };
          }
        });
      }

      // Transform payload.blanks back to customChoices for COMPREHENSION questions
      if (question.type === 'COMPREHENSION' && question.payload?.blanks && Array.isArray(question.payload.blanks)) {
        const customChoices: Record<string, string[]> = {};
        const correctIndex: Record<string, number> = {};

        question.payload.blanks.forEach((blank: any) => {
          const cleanName = blank.name.replace(/,\s*$/, '');
          customChoices[cleanName] = blank.choices || [];
          const correctIdx = blank.choices?.indexOf(blank.correctAnswer);
          correctIndex[cleanName] = correctIdx >= 0 ? correctIdx : 0;
        });

        transformedQuestion.customChoices = customChoices;
        transformedQuestion.correctIndex = correctIndex;
      }

      // Explicitly preserve payload to ensure it's not lost
      if (question.payload) {
        transformedQuestion.payload = question.payload;
      }

      return transformedQuestion;
    } catch (error) {
      console.error('Getting question by ID error:', error);
      throw error;
    }
  }

  // Delete question using deleteQuestion from @PikaDoApp/npm-pikado-logic
  async deleteQuestion(id: string): Promise<any> {
    try {
      console.log('Deleting question with pikado logic:', id);
      const result = await this.questionsNewRepository.deleteQuestion(id);

      return result;
    } catch (error) {
      console.error('Deleting question error:', error);
      throw error;
    }
  }
}
